import { min, round } from 'lodash';
import { useCallback } from 'react';

import {
    UseDeviceAlertConfigurationOptions,
    getDeviceAlertsConfiguration,
    useDeviceAlertConfiguration,
} from '@/api/customSensorAlerts/getDeviceAlertsConfigurations';
import { DeviceAlert, GetDeviceAlertConfigurationResponse } from '@/api/customSensorAlerts/types';
import { Device, useDevice } from '@/api/devices';
import { ConvertibleUnitPreferenceTypes, DEFAULT_UNITS, useDisplayPreferences } from '@/api/userAccount';
import { convert } from '@/common/convert';
import { QueryConfig } from '@/common/react-query';
import { getMaxDecimalPlaces, unitInContext } from '@/components/data/Datum';
import { useSpotterContext } from '@/context/SpotterContext';
import { triggerQuantityType } from '@/utils';

/** The unit conversion is this hook's `select`, so a caller cannot supply one of its own. */
type UseDeviceAlertsOptions = Omit<UseDeviceAlertConfigurationOptions, 'spotterId' | 'config'> & {
    config?: Omit<QueryConfig<typeof getDeviceAlertsConfiguration>, 'select'>;
};

/** The only part of the device the conversion reads, so the only part it needs to re-run for. */
const selectDeploymentConfig = (device: Device | undefined) => device?.deploymentConfig;

/**
 * Just like useDeviceAlertConfigurations but with units converted to user preferences. Gets spotterId from context and disables itself if we're viaOceansApi.
 */
export const useDeviceAlerts = (options: UseDeviceAlertsOptions) => {
    const { spotterId, viaOceansApi } = useSpotterContext();
    const enabled = options.config?.enabled !== false && !viaOceansApi;
    const { data: displayPreferences } = useDisplayPreferences({ config: { enabled } });
    const { data: deploymentConfig } = useDevice({ config: { enabled }, select: selectDeploymentConfig });

    const select = useCallback(
        (response: GetDeviceAlertConfigurationResponse): DeviceAlert[] =>
            response.data.map((trigger) => {
                const { trigger_details } = trigger;
                const { units, value, sensorPosition, unitPrecisionInfo } = trigger_details;
                const type = triggerQuantityType(trigger_details) as ConvertibleUnitPreferenceTypes;
                if (!(type in DEFAULT_UNITS)) {
                    return trigger;
                }
                const context = { deploymentConfig };
                const triggerUnits = unitInContext(type, units, context);
                const preferredUnits = unitInContext(type, displayPreferences?.[type] ?? DEFAULT_UNITS[type], context);
                if (!triggerUnits || !preferredUnits) {
                    return trigger;
                }
                const decimalPlaces = min([
                    getMaxDecimalPlaces(undefined, preferredUnits, type),
                    unitPrecisionInfo.decimalPlaces,
                ]);
                const convertedValue = convert(value).from(triggerUnits).to(preferredUnits);
                const roundedValue = round(convertedValue, decimalPlaces!);
                return {
                    ...trigger,
                    trigger_details: {
                        ...trigger_details,
                        value: roundedValue,
                        sensorPosition,
                    },
                };
            }),
        [deploymentConfig, displayPreferences]
    );

    return useDeviceAlertConfiguration({
        ...options,
        spotterId,
        config: { ...options.config, enabled, select },
    });
};
